import { useState, useRef, useEffect } from 'react';
import { usePlannerTodos } from '../../../utils/reactQuery/plannerTodos';
import { postPlannerTodos, putPlannerTodos, deletePlannerTodos, postPlannerTodosNotes, putPlannerTodosNotes, deletePlannerTodosNotes } from '../../../sevices/apiPlannerTodos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useCustomSearchParams } from '../../../hooks/useCustomSearchParams';

/**
 * Custom hook to manage task operations
 * @returns {Object} Task state and functions for task operations
 */
export const useTaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  
  // Notes dialog state
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  
  const [openDetailCard, setOpenDetailCard] = useState(false);
  const [mutateLoading, setMutateLoading] = useState(false);
  const [localTodos, setLocalTodos] = useState([]);
  const [optimisticTodoId, setOptimisticTodoId] = useState(null);

  const queryClient = useQueryClient();
  const [cardDetailId] = useCustomSearchParams('card_id');

  useEffect(() => {
    if (!cardDetailId) {
      setOpenDetailCard(false);
    }
  }, [cardDetailId]);

  const { mutate: createTodo } = useMutation({
    mutationFn: postPlannerTodos,
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      setOptimisticTodoId(null); // Clear optimistic todo tracking
      if (response.data.id ) {
        setTimeout(() => {
          handleReorderAfterCreation(response.data?.id, variables.after_todo);
        }, 100);
      } else {
        toast.success('Task created successfully');
      }
    },
    onError: (error) => {
      if (optimisticTodoId) {
        setLocalTodos(prevTodos => prevTodos.filter(todo => todo.id !== optimisticTodoId));
        queryClient.setQueryData(['todos'], (oldData) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter(todo => todo.id !== optimisticTodoId),
          };
        });
        setOptimisticTodoId(null);
      }
      toast.error(error?.message || 'Failed to create task');
    }
  });

  const { mutate: updateTodo } = useMutation({
    mutationFn: putPlannerTodos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
    },
    onError: (error) => {
      toast.error(error?.message);
    },
  });

  const { mutate: deleteTodo } = useMutation({
    mutationFn: deletePlannerTodos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      toast.success('Task deleted successfully');
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to delete task');
    }
  });

  const { mutate: createNote } = useMutation({
    mutationFn: postPlannerTodosNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      setIsNoteDialogOpen(false);
      setCurrentNote(null);
      setCurrentTaskId(null);
      setMutateLoading(false);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to create note');
      setMutateLoading(false);
    }
  });

  const { mutate: updateNoteMutate } = useMutation({
    mutationFn: putPlannerTodosNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
     setIsNoteDialogOpen(false);
     setCurrentNote(null);
     setCurrentTaskId(null);
     setMutateLoading(false);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to update note');
      setMutateLoading(false);
    }
  });

  const { mutate: deleteNoteMutate } = useMutation({
    mutationFn: deletePlannerTodosNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["todos"] });
      handleCloseNotes();
      setMutateLoading(false);
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to delete note');
      setMutateLoading(false);
    }
  });
  // Fetch tasks from API
  const { 
    todos, 
    isLoading, 
    error, 
    isError,
  } = usePlannerTodos();


  useEffect(() => {
    if (todos?.data) {
      const optimisticTodo = localTodos.find(t => t.isOptimistic);
      if (optimisticTodo) {
        const realTodo = todos.data.find(
          t =>
            t.title === optimisticTodo.title &&
            t.source_id == optimisticTodo.source_id &&
            !t.isOptimistic
        );
        if (realTodo) {
          const idx = localTodos.findIndex(t => t.id === optimisticTodo.id);
          const newTodos = [...todos.data];
          const realIdx = newTodos.findIndex(t => t.id === realTodo.id);
          if (realIdx !== -1) newTodos.splice(realIdx, 1);
          newTodos.splice(idx, 0, realTodo);
          setLocalTodos(newTodos);
          return;
        }
      }
      setLocalTodos(todos.data);
    }
  }, [todos?.data]);

  useEffect(() => {
    if (cardDetailId) {
      const cardExists = todos?.data?.some(
        (task) => task.source_id === cardDetailId
      );
      if (cardExists) {
        setOpenDetailCard(true);
      }
    }
  }, [cardDetailId,todos?.data]);

  /**
   * Toggle completion status of a task
   * @param {string} id - The id of the task to toggle
   */
  const handleToggleTask = (id,e) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed_at: e.target.checked ? new Date().toISOString() : null } : task
    ));

    // If using API data
    if (todos?.data) {
      const task = todos?.data.find(t => t.id === id);
      if (task) {
        updateTodo({
          id,
          isMove: false,
          data: {
            completed_at: e.target.checked ? new Date().toISOString() : null,
          },
        });
      }
    }
  };

  /**
   * Handle reordering after task creation (for card-to-task conversion)
   * @param {string} newTaskId - The ID of the newly created task
   * @param {string} afterTodoId - The ID of the task to place after
   */
  const handleReorderAfterCreation = (newTaskId, afterTodoId) => {
    if (!newTaskId) return;
    
    updateTodo({
      id: newTaskId,
      isMove: true,
      data: {
        after_todo: afterTodoId,
      },
    });
  };

  /**
   * Create a new task from a dropped card
   * @param {Object} cardData - The data from the dropped card
   */
  const createTaskFromCard = (cardData) => {
    if (!cardData || !cardData.title) {
      toast.error('Invalid card data');
      return;
    }

    const newTask = {
      title: cardData.title,
      after_todo: cardData?.after_todo || null,
      source_id: `${cardData.source_id}`,
      source_type: "TASKBOARD",
    };

    // Add additional data if available
    if (cardData.due_date) {
      newTask.due_date = cardData.due_date;
    }

    if (cardData?.completion_percentage) {
      newTask.completion_percentage = cardData.completion_percentage;
    }

    // Create the task via API
    if (todos?.data) {
      createTodo(newTask);
    } 
  };

  /**
   * Handle drag end event to reorder tasks
   * @param {Object} result - The drag result
   * @param {Array} filteredTasks - The filtered tasks array
   */
  const onDragEnd = (result, filteredTasks) => {
    if (filteredTasks) {
      const reorderedData = Array.from(filteredTasks);
      const [reorderedItem] = reorderedData.splice(result.source.index, 1);
      reorderedData.splice(result.destination.index, 0, reorderedItem);
      setLocalTodos(reorderedData);
      queryClient.setQueryData(['todos'], (oldData) => {
        if (!oldData?.data) return oldData;
        return { ...oldData, data: reorderedData };
      });
      const afterId = result.destination.index > 0 
        ? reorderedData[result.destination.index - 1].id 
        : null;
      updateTodo({
        id: reorderedItem.id,
        isMove: true,
        data: {
          after_todo: afterId,
        },
      });
    } else {
      const items = Array.from(tasks);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);
      setTasks(items);
    }
  };

  /**
   * Handle drag from right column to left column (card to task conversion)
   * @param {Object} result - The drag result
   * @param {Array} filteredTasks - The filtered tasks array
   * @param {Array} filteredCards - The filtered cards array
   */
  const handleCardToTaskDrag = (result, filteredTasks, filteredCards) => {
    const { destination,  draggableId } = result;
    
    // No validation needed - already checked in handleDragEnd
    const prepareData = filteredCards.find(
      (card) => card.card.card_id == draggableId
    )

    if (!prepareData) {
      toast.error('Card data not found');
      return;
    }

    // Calculate the after_todo ID based on destination position
    const afterId = destination?.index > 0 
      ? filteredTasks[destination?.index - 1].id 
      : null;

    const tempId = `temp-${Date.now()}`;
    const newTask = {
      id: tempId,
      title: prepareData.card.title,
      after_todo: afterId,
      source_id: draggableId,
      due_date: prepareData.card.due_date || null,
      source_type: "TASKBOARD",
      isOptimistic: true,
    };
    
    setOptimisticTodoId(tempId);
    
    const newTodos = [...localTodos];
    newTodos.splice(destination.index, 0, newTask);
    setLocalTodos(newTodos);
    queryClient.setQueryData(['todos'], (oldData) => {
      if (!oldData?.data) return oldData;
      return {
        ...oldData,
        data: newTodos,
      };
    });

    createTodo({
      title: prepareData.card.title,
      after_todo: afterId,
      source_id: draggableId,
      source_type: "TASKBOARD",
      due_date: prepareData.card.due_date || null,
    });
  };

  /**
   * Unified drag end handler that combines all drag scenarios
   * @param {Object} result - The drag result
   * @param {Array} filteredTasks - The filtered tasks array
   * @param {Array} filteredCards - The filtered cards array
   */
  const handleDragEnd = (result, filteredTasks, filteredCards) => {
    const { source, destination } = result;
    
    if (!result.destination) return;
    
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return; 
    }
    
    if (source.droppableId === destination.droppableId && source.droppableId === "left-tasks") {
      onDragEnd(result, filteredTasks);
    }
    else if (destination?.droppableId === "left-tasks" && source?.droppableId === "right-tasks") {
      handleCardToTaskDrag(result, filteredTasks, filteredCards);
    }
  };

  /**
   * Delete a task
   * @param {string} id - The id of the task to delete
   * @returns {Promise} A promise that resolves when deletion is successful
   */
  const handleDeleteTask = (id) => {
    // Return a promise that resolves when the deletion is successful
    return new Promise((resolve, reject) => {
      try {
        // Call the API to delete the task
        deleteTodo(id, {
          onSuccess: () => {
            resolve(); // Resolve the promise when deletion is successful
          },
          onError: (error) => {
            reject(error); // Reject the promise when deletion fails
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  };

  /**
   * Handle opening the notes dialog
   * @param {string} taskId - The id of the task to show notes for
   */
  const handleOpenNotes = (taskId) => {
    // Check if there's an existing note for this task
    const taskNote = todos?.data?.find(task => task.id === taskId)?.notes?.[0];
    
    if (taskNote) {
      // If note exists, show it
      setCurrentNote(taskNote);
    } else {
      // If no note exists, create a default empty one with task info
      const task = todos?.data?.find(t => t.id === taskId);
      setCurrentNote({
        title: task?.title ? `${task.title}` : '',
        content: '',
        date: new Date().toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }),
        taskId // Set taskId for new notes as well
      });
    }
    
    setCurrentTaskId(taskId);
    setIsNoteDialogOpen(true);
  };

  /**
   * Handle closing the notes dialog
   */
  const handleCloseNotes = (data) => {
    if (data) {
      handleSaveNote(data);
    }
    setIsNoteDialogOpen(false);
    setCurrentNote(null);
    setCurrentTaskId(null);
  };

  /**
   * Handle saving a note
   * @param {Object} updatedNote - The note to save
   */
  const handleSaveNote = (updatedNote) => {
    if (updatedNote.id) {
      // If the note already exists, update it
      updateNote(updatedNote.id, {
        ...updatedNote,
        taskId: currentTaskId
      });
    } else {
      // If it's a new note, add it
      addNote({
        ...updatedNote,
        taskId: currentTaskId
      });
    }
  };

  /**
   * Add a new note
   * @param {Object} note - The note to add
   */
  const addNote = (note) => {
    const newNote = {
      content: note.content || '',
      title: note.title || '',
      todo_id: note.taskId,
      ...(note?.color_code ? {color_code: note.color_code} : {})
    }
     function hasOnlyHtmlTags(input) {
        const textOnly = input.replace(/<[^>]*>/g, "");
        return textOnly.trim().length === 0;
      }
      const isEmptyHTML = hasOnlyHtmlTags(newNote?.content || '');

      if (!newNote.content.trim() || isEmptyHTML) {
        setIsNoteDialogOpen(false);
        setCurrentNote(null);
        setCurrentTaskId(null);
        return;
      }else{
        setMutateLoading(true);
        createNote(newNote);
      }
  };

  /**
   * Update an existing note
   * @param {string} id - The id of the note to update
   * @param {Object} updatedNote - The updated note data
   */
  const updateNote = (id, updatedNote) => {
    function hasOnlyHtmlTags(input) {
      const textOnly = input.replace(/<[^>]*>/g, "");
      return textOnly.trim().length === 0;
    }
    const isEmptyHTML = hasOnlyHtmlTags(updatedNote?.content || '');

    if (!updatedNote.content.trim() || isEmptyHTML) {
      setMutateLoading(true);
      deleteNoteMutate(id);
    } else {
      setMutateLoading(true);
      updateNoteMutate({
        id,
        data: {
          content: updatedNote.content, 
          title: updatedNote.title || '',
          ...(updatedNote?.color_code ? {color_code: updatedNote.color_code} : {})
        }
      });
    }
  };

  /**
   * Delete a note
   * @param {string} id - The id of the note to delete
   */
  const deleteNote = (id) => {
    setMutateLoading(true);
    deleteNoteMutate(id);
  };

  /**
   * Update the due date for a task
   * @param {string} taskId - The ID of the task
   * @param {Object} date - The moment.js date object
   */
  const updateDueDate = (taskId, date) => {
    if (!taskId) return;
    
    // Format the date as ISO string
    const formattedDate = date ? date.format() : null;
    
    // Call the API to update the task
    updateTodo({
      id: taskId,
      isMove: false,
      data: {
        due_date: formattedDate
      }
    });
    
    // Show success message
    toast.success(formattedDate ? 'Due date updated' : 'Due date removed');
  };

  return {
    tasks: todos?.data || tasks,
    handleToggleTask,
    onDragEnd,
    handleDragEnd, // New unified drag handler
    handleDeleteTask,
    createTaskFromCard,
    notes,
    addNote,
    updateNote,
    deleteNote,
    // Notes dialog state and functions
    isNoteDialogOpen,
    currentNote,
    currentTaskId,
    handleOpenNotes,
    handleCloseNotes,
    handleSaveNote,
    // Loading and error states
    isLoading,
    error,
    updateDueDate,
    openDetailCard,
    setOpenDetailCard,
    mutateLoading,
    localTodos
  };
};

export default useTaskManager; 